import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { getCurrentAuthUser } from '../../../auth/utils/auth-utils';
import { getClient } from '../../../api-utils/general-utils';
import { GetAccountDto } from '../../../api-utils/generated-client';
import {
  getSignatureDisclaimer,
  DocumentSection,
} from './document-utils';

import TitleSection from '../../../Header/title-section';
import Loader from '../../../widgets/Loader';
import PayroButton from '../../../widgets/PayroButton';
import ActualSignature from './ActualSignature';

import checkIcon from '../../../common-icons/checkIconGreen.png';

import {
  documentSectionsState,
  docVersionState,
  isNextButtonDisabledState,
  showDocsState,
  signatureDataUrlState,
  signedNewContractState,
} from '../../../recoil-state/application-stage-states';

import {
  accountRecordState,
  opportunityState,
  userContactInfoState,
  userInfoState,
} from '../../../recoil-state/general-states';

import { fundingStepState } from '../../../recoil-state/request-funding-states';

import './index.scss';

interface SignAgreementsProps {
  isTerm?: Function;
}

function SignNewContract({ isTerm }: SignAgreementsProps) {
  const [accountInfo, setAccountInfo] = useState<GetAccountDto>();
  const [userFullName, setUserFullName] = useState<string>('');
  const [gotToEndOfText, setGotToEndOfText] = useState(false);
  const [signedDocs, setSignedDocs] = useRecoilState(
    signedNewContractState,
  );
  const userContactInfo = useRecoilValue(userContactInfoState);
  const [checkedAgreeToSoftPull, setCheckedAgreeToSoftPull] =
    useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useRecoilState(
    signatureDataUrlState,
  );
  const [documentSections, setDocumentSections] = useRecoilState(
    documentSectionsState,
  );
  const [docVersion, setDocVersion] = useRecoilState(docVersionState);
  const [showDocs, setShowDocs] = useRecoilState(showDocsState);
  const [showLoader, setShowLoader] = useState(false);

  const AccountInfo = useRecoilValue(accountRecordState);
  const opportunities = useRecoilValue(opportunityState);

  useEffect(() => {
    const fetchData = async () => {
      const client = await getClient();
      if (!client) return;

      if (accountInfo?.need_updated_contract === false) {
        setSignedDocs(true);
      }

      const res = await client.documentsControllerGenerateDocuments();
      setDocumentSections(res.data.contractInfo as DocumentSection[]);
      setDocVersion(res.data.documentVersion);

      const authUser = await getCurrentAuthUser();
      setUserFullName(
        `${authUser.attributes.given_name} ${authUser.attributes.family_name}`,
      );
    };

    fetchData();
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (
      target.scrollHeight - target.scrollTop - 50 <=
      target.clientHeight
    ) {
      setGotToEndOfText(true);
    }
  };

  const signHandler = async () => {
    setShowLoader(true);

    const updatedAccount: GetAccountDto = {
      ...accountInfo!,
      need_updated_contract: false,
    };

    const client = await getClient();
    if (!client) return;
    //delete the exsisting signature so can save new one
    await client.documentsControllerDeleteSignature();
    await client.accountsControllerUpdate(updatedAccount);

    await client.documentsControllerSignDocuments({
      signature: signatureDataUrl,
      documentVersion: docVersion!,
      isUpdatedContract: true,
    });

    setSignedDocs(true);
    setShowDocs(false);
    window.location.reload();
  };

  if (!AccountInfo || showLoader)
    return <Loader message={showLoader ? 'Saving' : undefined} />;

  const renderDocumentSections = () =>
    documentSections.map((sec, idx) => {
      const Tag = sec.theTag as keyof JSX.IntrinsicElements;
      return <Tag key={idx}>{sec.theText}</Tag>;
    });

  return (
    <div>
      {AccountInfo.mca_account === false && (
        <div>
          <div id="contract-outer-container">
            <div id="contract-wrapper">
              <div id="contract-text" onScroll={handleScroll}>
                {renderDocumentSections()}
              </div>

              <div id="force-load-of-font">- - </div>

              {!gotToEndOfText ? (
                <p id="scroll-instructions">
                  {signedDocs
                    ? 'Scroll to read documents'
                    : 'Scroll and read the document to sign'}
                </p>
              ) : (
                <div>
                  <div
                    id="signature-wrapper"
                    className="rounded-corner-section"
                  >
                    <p id="your-signature-label">
                      Your Generated Signature
                    </p>
                    <ActualSignature
                      fullName={
                        userContactInfo.first_name +
                        ' ' +
                        userContactInfo.last_name
                      }
                      getDataUrlCb={setSignatureDataUrl}
                    />
                  </div>

                  <div className="checkboxdata-message-non-ny-new">
                    <input
                      type="checkbox"
                      onChange={() =>
                        setCheckedAgreeToSoftPull(
                          !checkedAgreeToSoftPull,
                        )
                      }
                      className="checkbox-design"
                    />
                    <p className="checkboxdata">
                      Soft credit pull authorization
                    </p>
                  </div>

                  <div className="agreementCreditNonNy">
                    <img src="info.svg" alt="info" />
                    <p id="scroll-credit-non-ny">
                      A soft credit pull will not impact your credit,
                      Payro will not do a hard credit pull.
                    </p>
                  </div>

                  <p id="signature-disclaimer">
                    {getSignatureDisclaimer(
                      AccountInfo.legal_name || '',
                    )}
                  </p>

                  <PayroButton
                    variant={signedDocs ? 'green' : 'green-agree'}
                    startIcon={signedDocs ? checkIcon : undefined}
                    disabled={
                      !gotToEndOfText ||
                      (AccountInfo.mca_account === false &&
                        !checkedAgreeToSoftPull) ||
                      signedDocs
                    }
                    customHeight="50px"
                    customWidth="width-200"
                    centered
                    onClick={signHandler}
                  >
                    {signedDocs
                      ? 'Your Signature Has Been Recorded'
                      : 'I Agree & Sign'}
                  </PayroButton>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignNewContract;
